import { useEffect, useState } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Minus, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'

const MIN_ZOOM = 0.5
const MAX_ZOOM = 4
const ZOOM_STEP = 0.25

function clampZoom(value: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value))
}

/**
 * In-app image viewer that never changes the route or opens a browser tab.
 *
 * Radix keeps focus/scroll restoration local to the dialog, so closing this returns the
 * person to the exact screen, drawer, filter and scroll position they were using before.
 */
export function ImageLightbox({
  open,
  onOpenChange,
  src,
  alt,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  src: string | null
  alt: string
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {open ? <ImageLightboxContent key={src ?? 'image'} src={src} alt={alt} /> : null}
    </DialogPrimitive.Root>
  )
}

function ImageLightboxContent({ src, alt }: { src: string | null; alt: string }) {
  const [zoom, setZoom] = useState(1)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === '+' || event.key === '=') {
        event.preventDefault()
        setZoom((value) => clampZoom(value + ZOOM_STEP))
      } else if (event.key === '-') {
        event.preventDefault()
        setZoom((value) => clampZoom(value - ZOOM_STEP))
      } else if (event.key === '0') {
        event.preventDefault()
        setZoom(1)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-modal bg-black/85 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
      <DialogPrimitive.Content
        aria-describedby={undefined}
        className="fixed inset-0 z-modal flex flex-col outline-none"
      >
        <DialogPrimitive.Title className="sr-only">{alt}</DialogPrimitive.Title>

        <div className="flex shrink-0 items-center justify-between gap-3 p-3 sm:p-4">
          <DialogPrimitive.Close asChild>
            <Button
              type="button"
              variant="secondary"
              size="icon"
              className="rounded-full bg-black/55 text-white hover:bg-black/75 hover:text-white"
              aria-label="Close image"
              title="Close image"
            >
              <X className="size-5" />
            </Button>
          </DialogPrimitive.Close>

          <div className="flex items-center gap-1 rounded-full bg-black/55 p-1 text-white">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="rounded-full text-white hover:bg-white/15 hover:text-white"
              disabled={zoom <= MIN_ZOOM}
              onClick={() => setZoom((value) => clampZoom(value - ZOOM_STEP))}
              aria-label="Zoom out"
              title="Zoom out"
            >
              <Minus className="size-4" />
            </Button>
            <button
              type="button"
              onClick={() => setZoom(1)}
              className="min-w-14 rounded-full px-2 py-1 text-xs font-medium tabular-nums hover:bg-white/15"
              aria-label="Reset zoom"
              title="Reset zoom"
            >
              {Math.round(zoom * 100)}%
            </button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="rounded-full text-white hover:bg-white/15 hover:text-white"
              disabled={zoom >= MAX_ZOOM}
              onClick={() => setZoom((value) => clampZoom(value + ZOOM_STEP))}
              aria-label="Zoom in"
              title="Zoom in"
            >
              <Plus className="size-4" />
            </Button>
          </div>
        </div>

        <div
          className="min-h-0 flex-1 overflow-auto px-3 pb-3 sm:px-6 sm:pb-6"
          onDoubleClick={() => setZoom((value) => (value === 1 ? 2 : 1))}
        >
          <div
            className="flex min-h-full min-w-full items-center justify-center"
            style={{
              width: `${Math.max(1, zoom) * 100}%`,
              height: `${Math.max(1, zoom) * 100}%`,
            }}
          >
            {src ? (
              <img
                src={src}
                alt={alt}
                draggable={false}
                className="select-none object-contain shadow-2xl"
                style={{
                  maxWidth: zoom <= 1 ? `${zoom * 100}%` : '100%',
                  maxHeight: zoom <= 1 ? `${zoom * 100}%` : '100%',
                }}
              />
            ) : null}
          </div>
        </div>

        <p className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/55 px-3 py-1 text-center text-[11px] text-white/80">
          Double-click to zoom · + / − to adjust · Esc to close
        </p>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}
